import { FK_PARSERS, GENERIC_JOURNAL_HINTS, IFkJournalHints } from "config/fkParsers";

/**
 * Wspólna logika budowy dokumentów z dziennika księgowań (CSV/BE) — używana przez
 * rejestr „Dziennik księgowań" (widok drzewa) oraz listę „Polecenia księgowania"
 * (dokumenty PK) na połączonej liście „Dokumenty księgowe".
 */

export interface JournalRow {
  id: string;
  path: string[];
  typDokumentu: string;
  nrDokumentu: string;
  dataKsiegowania: string;
  opis: string;
  kontoWn: string;
  kontoMa: string;
  kwota: number | null;
  kontrahent?: string;
}

export interface CsvPayload {
  v?: number; // wersja formatu (brak lub <2 = stare dane)
  headers: string[];
  rows: Record<string, unknown>[];
  importedAt: number;
  fileName: string;
  systemId?: string;
}

export interface JournalData {
  allRows: JournalRow[];
  docRows: JournalRow[];
  source: "csv" | "mock";
  fileName?: string;
  mappingOk?: boolean;
  detectedCols?: Record<string, string | null>;
}

export const JOURNALS_ROWS_KEY = "zois_journals_rows";
export const CONTRACTORS_ROWS_KEY = "zois_contractors_rows";

// Wzorzec kont kontrahentów rozrachunkowych (201-XX lub 211-XX)
export const CONTRACTOR_ACCT_RE = /^2[01]1-(\d+)/;

export function loadContractorsMap(): Map<string, string> {
  const result = new Map<string, string>();

  // Źródło 1: dedykowana kartoteka kontrahentów (wgrana przez użytkownika)
  try {
    const raw = localStorage.getItem(CONTRACTORS_ROWS_KEY);
    if (raw) {
      const { entries } = JSON.parse(raw) as { entries: { code: string; name: string }[] };
      entries.forEach((e) => { if (e.code && e.name) result.set(e.code.toLowerCase(), e.name); });
    }
  } catch { /* ignore */ }

  // Źródło 2 (fallback): bilans otwarcia — zawiera konta 201-XX / 211-XX z nazwami
  if (result.size === 0) {
    try {
      const raw = localStorage.getItem("zois_opening_balance_v1");
      if (raw) {
        const { entries } = JSON.parse(raw) as { entries: { code: string; name: string }[] };
        entries
          .filter((e) => CONTRACTOR_ACCT_RE.test(e.code.toLowerCase()))
          .forEach((e) => { if (e.name) result.set(e.code.toLowerCase(), e.name); });
      }
    } catch { /* ignore */ }
  }

  return result;
}

// ── Mapowanie kolumn CSV na podstawie hints FK parsera ────────────────────────

function detectMapping(headers: string[], hints: IFkJournalHints): Record<keyof IFkJournalHints, string | null> {
  const norm = headers.map((h) => h.toLowerCase().trim());
  const fields = ["nrDokumentu", "dataKsiegowania", "typDokumentu", "opis", "kontoWn", "kontoMa", "kwota"] as const;
  const result = {} as Record<keyof IFkJournalHints, string | null>;
  const used = new Set<number>();

  for (const field of fields) {
    result[field] = null;
    for (const pat of hints[field] as string[]) {
      const patNorm = pat.toLowerCase().trim();
      const idx = norm.findIndex((h, i) => {
        if (used.has(i)) return false;
        if (h === patNorm) return true;
        if (h.includes(patNorm)) return true;
        if (h.length >= 3 && patNorm.includes(h)) return true;
        return false;
      });
      if (idx !== -1) {
        result[field] = headers[idx];
        used.add(idx);
        break;
      }
    }
  }
  result.pairMode = null;
  return result;
}

function getJournalHints(systemId?: string): IFkJournalHints {
  return FK_PARSERS[systemId ?? ""]?.journalHints ?? GENERIC_JOURNAL_HINTS;
}

// ── Parsery wartości ──────────────────────────────────────────────────────────

export function parseKwota(v: unknown): number | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return isNaN(v) ? null : v;
  const s = String(v).trim();
  const hasCommaAndDot = s.includes(",") && s.includes(".");
  const cleaned = hasCommaAndDot
    ? s.replace(/,/g, "")
    : s.replace(/\s/g, "").replace(",", ".");
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

export function parseDate(v: unknown): string {
  if (!v) return "";
  const s = String(v).trim();
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return "";
}

// ── Budowanie hierarchii z płaskiego CSV ──────────────────────────────────────

export function buildFromCsv(payload: CsvPayload, overrideContractors?: Map<string, string>): JournalData {
  const hints = getJournalHints(payload.systemId);
  const map = detectMapping(payload.headers, hints);
  const contractorsMap = overrideContractors ?? loadContractorsMap();

  const mappingOk = !!(map.nrDokumentu && (map.kontoWn || map.kontoMa));

  if (!mappingOk) {
    console.warn("[Dziennik] Nie udało się zmapować kolumn CSV.", {
      dostępneKolumny: payload.headers,
      systemId: payload.systemId,
      zmapowano: map,
    });
    return {
      allRows: [], docRows: [],
      source: "csv", fileName: payload.fileName,
      mappingOk: false,
      detectedCols: map as Record<string, string | null>,
    };
  }

  const docMap = new Map<string, { csvRows: Record<string, unknown>[]; date: string; typ: string }>();
  for (const csvRow of payload.rows) {
    const nr = String(csvRow[map.nrDokumentu!] ?? "").trim();
    if (!nr) continue;
    if (!docMap.has(nr)) {
      docMap.set(nr, {
        csvRows: [],
        date: parseDate(map.dataKsiegowania ? csvRow[map.dataKsiegowania] : ""),
        typ: map.typDokumentu ? String(csvRow[map.typDokumentu] ?? "").trim() : "",
      });
    }
    docMap.get(nr)!.csvRows.push(csvRow);
  }

  const allRows: JournalRow[] = [];
  const docRows: JournalRow[] = [];
  let idx = 0;

  for (const [nrDoc, { csvRows, date, typ }] of docMap) {
    const docId = `d${idx++}`;

    let kwotaSum = 0;
    for (const r of csvRows) {
      const hasWn = map.kontoWn && String(r[map.kontoWn] ?? "").trim();
      if (hasWn) { const k = parseKwota(map.kwota ? r[map.kwota] : null); if (k != null) kwotaSum += k; }
    }
    if (kwotaSum === 0) {
      for (const r of csvRows) { const k = parseKwota(map.kwota ? r[map.kwota] : null); if (k != null) kwotaSum += k; }
    }

    const opisy = csvRows.map((r) => map.opis ? String(r[map.opis] ?? "").trim() : "").filter(Boolean);
    const opisSet = new Set(opisy);
    const headerOpis = opisSet.size === 1 ? [...opisSet][0] : nrDoc;

    const rowContractors: (string | undefined)[] = csvRows.map((csvRow) => {
      if (typ === "PK" || contractorsMap.size === 0) return undefined;
      for (const colKey of [map.kontoWn, map.kontoMa]) {
        if (!colKey) continue;
        const konto = String(csvRow[colKey] ?? "").trim().toLowerCase();
        if (CONTRACTOR_ACCT_RE.test(konto)) return contractorsMap.get(konto);
      }
      return undefined;
    });

    const distinctContractors = new Set(rowContractors.filter(Boolean) as string[]);
    const headerKontrahent = distinctContractors.size === 1 ? [...distinctContractors][0] : undefined;

    const header: JournalRow = {
      id: docId,
      path: [docId],
      typDokumentu: typ,
      nrDokumentu: nrDoc,
      dataKsiegowania: date,
      opis: headerOpis,
      kontoWn: "",
      kontoMa: "",
      kwota: kwotaSum || null,
      kontrahent: headerKontrahent,
    };
    allRows.push(header);
    docRows.push(header);

    csvRows.forEach((csvRow, i) => {
      allRows.push({
        id: `${docId}-${i}`,
        path: [docId, String(i)],
        typDokumentu: "",
        nrDokumentu: "",
        dataKsiegowania: "",
        opis: map.opis ? String(csvRow[map.opis] ?? "").trim() : "",
        kontoWn: map.kontoWn ? String(csvRow[map.kontoWn] ?? "").trim() : "",
        kontoMa: map.kontoMa ? String(csvRow[map.kontoMa] ?? "").trim() : "",
        kwota: parseKwota(map.kwota ? csvRow[map.kwota] : null),
        kontrahent: distinctContractors.size > 1 ? rowContractors[i] : undefined,
      });
    });
  }

  return {
    allRows, docRows,
    source: "csv", fileName: payload.fileName,
    mappingOk: true,
    detectedCols: map as Record<string, string | null>,
  };
}

// ── Dane demonstracyjne (fallback gdy brak CSV) ───────────────────────────────

export const formatKwota = (v: number | null) =>
  v == null ? "" : new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

export const DOC_TYPES: Record<string, { label: string; color: string; bg: string }> = {
  "Faktura KSeF":       { label: "KSeF",   color: "#5d49c7", bg: "rgba(93,73,199,0.1)"  },
  "Faktura zakupowa":   { label: "FV Zak", color: "#1565c0", bg: "rgba(21,101,192,0.1)" },
  "Rachunek zakupowy":  { label: "Rach.",  color: "#00695c", bg: "rgba(0,105,92,0.1)"   },
  "Operacja finansowa": { label: "Bank",   color: "#e65100", bg: "rgba(230,81,0,0.1)"   },
};

const MOCK_ALL_ROWS: JournalRow[] = [
  { id: "d1",   path: ["d1"],     typDokumentu: "Faktura KSeF",       nrDokumentu: "KSeF/2024/001",   dataKsiegowania: "2024-01-15", opis: "Zakup materiałów biurowych — Biuro Plus Sp. z o.o.", kontoWn: "", kontoMa: "", kwota: 1230.00 },
  { id: "d1-1", path: ["d1","1"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Koszty materiałów i wyposażenia",  kontoWn: "401", kontoMa: "",    kwota: 1000.00 },
  { id: "d1-2", path: ["d1","2"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "VAT naliczony (23%)",              kontoWn: "221", kontoMa: "",    kwota: 230.00  },
  { id: "d1-3", path: ["d1","3"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Zobowiązanie wobec dostawcy",      kontoWn: "",    kontoMa: "210", kwota: 1230.00 },
  { id: "d2",   path: ["d2"],     typDokumentu: "Faktura zakupowa",   nrDokumentu: "FV/ZAK/2024/001", dataKsiegowania: "2024-01-20", opis: "Usługi IT — Comarch SA", kontoWn: "", kontoMa: "", kwota: 6150.00 },
  { id: "d2-1", path: ["d2","1"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Usługi obce — wdrożenie systemu",  kontoWn: "402", kontoMa: "",    kwota: 5000.00 },
  { id: "d2-2", path: ["d2","2"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "VAT naliczony (23%)",              kontoWn: "221", kontoMa: "",    kwota: 1150.00 },
  { id: "d2-3", path: ["d2","3"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Zobowiązanie wobec dostawcy",      kontoWn: "",    kontoMa: "210", kwota: 6150.00 },
  { id: "d3",   path: ["d3"],     typDokumentu: "Operacja finansowa", nrDokumentu: "OF/2024/001",     dataKsiegowania: "2024-01-25", opis: "Przelew — spłata faktury KSeF/2024/001", kontoWn: "", kontoMa: "", kwota: 1230.00 },
  { id: "d3-1", path: ["d3","1"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Rozliczenie zobowiązania",          kontoWn: "210", kontoMa: "",    kwota: 1230.00 },
  { id: "d3-2", path: ["d3","2"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Rachunek bankowy",                  kontoWn: "",    kontoMa: "131", kwota: 1230.00 },
  { id: "d4",   path: ["d4"],     typDokumentu: "Faktura KSeF",       nrDokumentu: "KSeF/2024/002",   dataKsiegowania: "2024-02-01", opis: "Usługi doradztwa podatkowego — TaxPro Sp. k.", kontoWn: "", kontoMa: "", kwota: 3690.00 },
  { id: "d4-1", path: ["d4","1"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Usługi doradcze",                   kontoWn: "402", kontoMa: "",    kwota: 3000.00 },
  { id: "d4-2", path: ["d4","2"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "VAT naliczony (23%)",               kontoWn: "221", kontoMa: "",    kwota: 690.00  },
  { id: "d4-3", path: ["d4","3"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Zobowiązanie wobec dostawcy",       kontoWn: "",    kontoMa: "210", kwota: 3690.00 },
  { id: "d5",   path: ["d5"],     typDokumentu: "Rachunek zakupowy",  nrDokumentu: "RACH/2024/001",   dataKsiegowania: "2024-02-05", opis: "Tłumaczenie dokumentów — TransLex", kontoWn: "", kontoMa: "", kwota: 800.00 },
  { id: "d5-1", path: ["d5","1"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Usługi tłumaczeniowe",              kontoWn: "402", kontoMa: "",    kwota: 800.00  },
  { id: "d5-2", path: ["d5","2"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Zobowiązanie wobec dostawcy",       kontoWn: "",    kontoMa: "210", kwota: 800.00  },
  { id: "d6",   path: ["d6"],     typDokumentu: "Operacja finansowa", nrDokumentu: "OF/2024/002",     dataKsiegowania: "2024-02-10", opis: "Przelew — spłata faktury FV/ZAK/2024/001", kontoWn: "", kontoMa: "", kwota: 6150.00 },
  { id: "d6-1", path: ["d6","1"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Rozliczenie zobowiązania",           kontoWn: "210", kontoMa: "",    kwota: 6150.00 },
  { id: "d6-2", path: ["d6","2"], typDokumentu: "", nrDokumentu: "", dataKsiegowania: "", opis: "Rachunek bankowy",                   kontoWn: "",    kontoMa: "131", kwota: 6150.00 },
];

export const MOCK_DATA: JournalData = {
  allRows: MOCK_ALL_ROWS,
  docRows: MOCK_ALL_ROWS.filter((r) => r.path.length === 1),
  source: "mock",
};

// ── Ładowanie danych z localStorage ───────────────────────────────────────────

export function loadJournalFromStorage(): JournalData | null {
  try {
    const raw = localStorage.getItem(JOURNALS_ROWS_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw) as CsvPayload;
    if (!payload.v || payload.v < 2) {
      localStorage.removeItem(JOURNALS_ROWS_KEY);
      return null;
    }
    return buildFromCsv(payload);
  } catch {
    return null;
  }
}

/** Czy dokument jest poleceniem księgowania (PK). */
export const isPostingOrder = (typDokumentu: string): boolean =>
  /\bpk\b|polecenie/i.test((typDokumentu || "").trim());
